import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Trash2,
  Shield,
  UserCog,
  Key,
  User,
  Save,
  Eye,
  EyeOff,
  AlertTriangle,
  RefreshCcw,
  Pencil,
  MapPin,
  Navigation,
  Minus,
  Search
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const { user: currentUser, updateUser } = useAuth();

  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    username: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Reset confirmation state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetting, setResetting] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'manager'
  });

  // Edit user state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    username: '',
    password: '',
    role: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Geofence state
  const [geofenceSettings, setGeofenceSettings] = useState({
    enabled: false,
    latitude: 0,
    longitude: 0,
    radius_meters: 100,
    address: ''
  });
  const [savingGeofence, setSavingGeofence] = useState(false);
  const [locatingUser, setLocatingUser] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGeofenceSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/geofence`);
      setGeofenceSettings(response.data);
    } catch (error) {
      // Non-blocking; defaults remain
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchGeofenceSettings();
  }, [fetchUsers, fetchGeofenceSettings]);

  useEffect(() => {
    if (currentUser) {
      setProfileData(prev => ({
        ...prev,
        username: currentUser.username || ''
      }));
    }
  }, [currentUser]);

  const handleSaveGeofence = async () => {
    setSavingGeofence(true);
    try {
      await axios.put(`${API}/geofence`, geofenceSettings);
      toast.success('Geofence settings saved successfully');
      // Collapse the geofence section after saving
      setGeofenceExpanded(false);
    } catch (error) {
      toast.error('Failed to save geofence settings');
    } finally {
      setSavingGeofence(false);
    }
  };

  // State to control geofence section expansion
  const [geofenceExpanded, setGeofenceExpanded] = useState(false);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setLocatingUser(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeofenceSettings(prev => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }));
        setLocatingUser(false);
        toast.success('Location updated to your current position');
      },
      (error) => {
        setLocatingUser(false);
        toast.error('Unable to get your location. Please check permissions.');
      },
      { enableHighAccuracy: true }
    );
  };

  const [searchingAddress, setSearchingAddress] = useState(false);

  const searchAddress = async () => {
    if (!geofenceSettings.address.trim()) {
      toast.error('Please enter an address to search');
      return;
    }

    setSearchingAddress(true);
    try {
      // Use OpenStreetMap Nominatim for free geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(geofenceSettings.address)}&limit=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'VPrepTiffinApp/1.0'
          }
        }
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setGeofenceSettings(prev => ({
          ...prev,
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
          address: result.display_name
        }));
        toast.success('Address found! Coordinates updated.');
      } else {
        toast.error('Address not found. Please try a more specific address.');
      }
    } catch (error) {
      toast.error('Failed to search address. Please try again.');
    } finally {
      setSearchingAddress(false);
    }
  };

  const handleProfileSave = async () => {
    // Validate
    if (profileData.new_password && profileData.new_password !== profileData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (profileData.new_password && !profileData.current_password) {
      toast.error('Current password is required to change password');
      return;
    }

    if (profileData.new_password && profileData.new_password.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }

    setSavingProfile(true);
    try {
      const payload = {};
      if (profileData.username !== currentUser.username) {
        payload.username = profileData.username;
      }
      if (profileData.new_password) {
        payload.current_password = profileData.current_password;
        payload.new_password = profileData.new_password;
      }

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setEditingProfile(false);
        return;
      }

      const response = await axios.put(`${API}/auth/profile`, payload);
      toast.success('Profile updated successfully');
      
      // Update local auth context if username changed
      if (response.data.user) {
        updateUser(response.data.user);
      }
      
      // Reset password fields
      setProfileData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      setEditingProfile(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.username.trim() || !formData.password.trim()) {
      toast.error('Username and password are required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('User created successfully');
      setShowDialog(false);
      setFormData({ username: '', password: '', role: 'manager' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error('You cannot delete yourself');
      return;
    }

    if (!window.confirm(`Delete user "${user.username}"?`)) return;

    try {
      await axios.delete(`${API}/users/${user.id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      username: user.username,
      password: '',
      role: user.role
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.username.trim()) {
      toast.error('Username is required');
      return;
    }

    setSavingEdit(true);
    try {
      const payload = {};
      if (editFormData.username !== editingUser.username) {
        payload.username = editFormData.username;
      }
      if (editFormData.password.trim()) {
        payload.password = editFormData.password;
      }
      if (editFormData.role !== editingUser.role) {
        payload.role = editFormData.role;
      }

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setShowEditDialog(false);
        return;
      }

      await axios.put(`${API}/users/${editingUser.id}`, payload);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      setEditingUser(null);
      fetchUsers();
      
      // If editing self, update auth context
      if (editingUser.id === currentUser?.id && payload.username) {
        updateUser({ ...currentUser, username: payload.username });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResetEverything = async () => {
    if (resetStep === 1) {
      setResetStep(2);
      return;
    }

    // Step 2 confirmed - perform reset
    setResetting(true);
    try {
      await axios.post(`${API}/reset-everything`);
      toast.success('All data has been reset successfully');
      setShowResetDialog(false);
      setResetStep(1);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset data');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight" >
            Settings
          </h1>
          <p className="text-stone-500 mt-1">Manage your account and app settings</p>
        </div>
        <button
          onClick={() => setShowDialog(true)}
          className="btn-primary"
          data-testid="add-user-btn"
        >
          <Plus size={20} />
          <span>Add User</span>
        </button>
      </div>

      {/* My Profile Section */}
      <div className="virsa-card p-6" data-testid="my-profile-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#124242] flex items-center justify-center text-white text-xl font-semibold">
              {currentUser?.username?.[0]?.toUpperCase() || 'A'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-stone-800">My Profile</h2>
              <p className="text-sm text-stone-500">Manage your account settings</p>
            </div>
          </div>
          {!editingProfile && (
            <button
              onClick={() => setEditingProfile(true)}
              className="btn-outline text-sm"
              data-testid="edit-profile-btn"
            >
              <User size={16} />
              <span>Edit Profile</span>
            </button>
          )}
        </div>

        {editingProfile ? (
          <div className="space-y-4 mt-4 border-t border-stone-200 pt-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">
                <User size={14} className="inline mr-1" />
                Username
              </label>
              <input
                type="text"
                value={profileData.username}
                onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                className="virsa-input"
                placeholder="Enter username"
                data-testid="profile-username"
              />
            </div>

            {/* Password Change Section */}
            <div className="border-t border-stone-100 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                <Key size={14} />
                Change Password
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={profileData.current_password}
                      onChange={(e) => setProfileData({ ...profileData, current_password: e.target.value })}
                      className="virsa-input pr-10"
                      placeholder="Enter current password"
                      data-testid="profile-current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={profileData.new_password}
                      onChange={(e) => setProfileData({ ...profileData, new_password: e.target.value })}
                      className="virsa-input pr-10"
                      placeholder="Enter new password"
                      data-testid="profile-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={profileData.confirm_password}
                    onChange={(e) => setProfileData({ ...profileData, confirm_password: e.target.value })}
                    className="virsa-input"
                    placeholder="Confirm new password"
                    data-testid="profile-confirm-password"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <button
                onClick={() => {
                  setEditingProfile(false);
                  setProfileData({
                    username: currentUser?.username || '',
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}
                className="btn-outline"
                data-testid="cancel-profile-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleProfileSave}
                disabled={savingProfile}
                className="btn-primary"
                data-testid="save-profile-btn"
              >
                {savingProfile ? <span className="spinner" /> : <Save size={18} />}
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 mt-4 border-t border-stone-200 pt-4">
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Username</p>
              <p className="font-medium text-stone-800">{currentUser?.username}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Role</p>
              <span className="badge badge-teal mt-1">
                <Shield size={12} className="mr-1" />
                {currentUser?.role === 'admin' ? 'Admin' : 'Manager'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Geofence Settings - Admin Only */}
      {currentUser?.role === 'admin' && (
        <div className="virsa-card p-6">
          <button 
            onClick={() => setGeofenceExpanded(!geofenceExpanded)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#124242] flex items-center justify-center">
                <MapPin size={20} className="text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-xl font-bold text-stone-800">Geofence Settings</h2>
                <p className="text-sm text-stone-500">
                  {geofenceSettings.enabled 
                    ? `Enabled • ${geofenceSettings.radius_meters}m radius` 
                    : 'Restrict manager access to workplace location'}
                </p>
              </div>
            </div>
            <div className={`transform transition-transform ${geofenceExpanded ? 'rotate-180' : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-stone-400">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
          </button>

          {geofenceExpanded && (
            <div className="mt-4 pt-4 border-t border-stone-200">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg mb-4">
                <div>
                  <p className="font-medium text-stone-800">Enable Geofencing</p>
                  <p className="text-sm text-stone-500">When enabled, managers can only access the app from within the specified area</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setGeofenceSettings(prev => ({ ...prev, enabled: !prev.enabled }));
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    geofenceSettings.enabled ? 'bg-[#124242]' : 'bg-stone-300'
                  }`}
                  data-testid="geofence-enabled-toggle"
                >
                  <span 
                    className={`absolute top-0.5 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                      geofenceSettings.enabled ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {geofenceSettings.enabled && (
                <div className="space-y-4">
              {/* Address Input with Search */}
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-2">
                  Workplace Address
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={geofenceSettings.address}
                    onChange={(e) => setGeofenceSettings(prev => ({ ...prev, address: e.target.value }))}
                    onKeyPress={(e) => e.key === 'Enter' && searchAddress()}
                    className="virsa-input flex-1"
                    placeholder="Enter address (e.g., 123 Main St, Toronto, ON)"
                    data-testid="geofence-address"
                  />
                  <button
                    onClick={searchAddress}
                    disabled={searchingAddress}
                    className="btn-primary px-4"
                    data-testid="search-address-btn"
                  >
                    {searchingAddress ? (
                      <span className="spinner" />
                    ) : (
                      <MapPin size={18} />
                    )}
                    <span className="hidden sm:inline ml-2">Search</span>
                  </button>
                </div>
                <p className="text-xs text-stone-400 mt-1">
                  Enter an address and click Search to auto-fill coordinates
                </p>
              </div>

              {/* Coordinates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={geofenceSettings.latitude}
                    onChange={(e) => setGeofenceSettings(prev => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }))}
                    className="virsa-input"
                    placeholder="e.g., 43.6532"
                    data-testid="geofence-latitude"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={geofenceSettings.longitude}
                    onChange={(e) => setGeofenceSettings(prev => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }))}
                    className="virsa-input"
                    placeholder="e.g., -79.3832"
                    data-testid="geofence-longitude"
                  />
                </div>
              </div>

              {/* Use Current Location Button */}
              <button
                onClick={getCurrentLocation}
                disabled={locatingUser}
                className="btn-outline w-full justify-center"
                data-testid="use-current-location-btn"
              >
                {locatingUser ? (
                  <span className="spinner" />
                ) : (
                  <Navigation size={18} />
                )}
                <span>{locatingUser ? 'Getting location...' : 'Use My Current Location'}</span>
              </button>

              {/* Radius Slider */}
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-2">
                  Allowed Radius: <span className="font-bold text-[#124242]">{geofenceSettings.radius_meters} meters</span>
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setGeofenceSettings(prev => ({ ...prev, radius_meters: Math.max(50, prev.radius_meters - 50) }))}
                    className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors"
                    data-testid="radius-decrease"
                  >
                    <Minus size={20} />
                  </button>
                  <input
                    type="range"
                    min="50"
                    max="1000"
                    step="50"
                    value={geofenceSettings.radius_meters}
                    onChange={(e) => setGeofenceSettings(prev => ({ ...prev, radius_meters: parseInt(e.target.value) }))}
                    className="flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-[#124242]"
                    data-testid="radius-slider"
                  />
                  <button
                    onClick={() => setGeofenceSettings(prev => ({ ...prev, radius_meters: Math.min(1000, prev.radius_meters + 50) }))}
                    className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors"
                    data-testid="radius-increase"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <div className="flex justify-between text-xs text-stone-400 mt-1">
                  <span>50m</span>
                  <span>500m</span>
                  <span>1000m</span>
                </div>
              </div>

              {/* Map Preview */}
              {geofenceSettings.latitude !== 0 && geofenceSettings.longitude !== 0 && (
                <div className="rounded-lg overflow-hidden border border-stone-200">
                  <iframe
                    title="Location Map"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${geofenceSettings.longitude - 0.01},${geofenceSettings.latitude - 0.01},${geofenceSettings.longitude + 0.01},${geofenceSettings.latitude + 0.01}&layer=mapnik&marker=${geofenceSettings.latitude},${geofenceSettings.longitude}`}
                  />
                  <p className="text-xs text-stone-500 p-2 bg-stone-50 text-center">
                    📍 Lat: {geofenceSettings.latitude.toFixed(6)}, Lng: {geofenceSettings.longitude.toFixed(6)} | Radius: {geofenceSettings.radius_meters}m
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end mt-4 pt-4 border-t border-stone-200">
            <button
              onClick={handleSaveGeofence}
              disabled={savingGeofence}
              className="btn-primary"
              data-testid="save-geofence-btn"
            >
              {savingGeofence ? <span className="spinner" /> : <Save size={18} />}
              <span>Save Geofence Settings</span>
            </button>
          </div>
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      <div className="virsa-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="virsa-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Created</th>
                <th className="w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} data-testid={`user-row-${user.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#124242] flex items-center justify-center text-white font-semibold">
                        {user.username[0].toUpperCase()}
                      </div>
                      <span className="font-medium">{user.username}</span>
                      {user.id === currentUser?.id && (
                        <span className="badge badge-gold">You</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${user.role === 'admin' ? 'badge-error' : 'badge-teal'}`}>
                      {user.role === 'admin' ? (
                        <><Shield size={12} className="mr-1" /> Admin</>
                      ) : (
                        <><UserCog size={12} className="mr-1" /> Manager</>
                      )}
                    </span>
                  </td>
                  <td className="text-stone-500">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 text-[#124242] hover:bg-[#124242]/10 rounded-lg transition-colors"
                        data-testid={`edit-user-${user.id}`}
                        title="Edit user"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        disabled={user.id === currentUser?.id}
                        className={`p-2 rounded-lg transition-colors ${
                          user.id === currentUser?.id
                            ? 'text-stone-300 cursor-not-allowed'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                        data-testid={`delete-user-${user.id}`}
                        title={user.id === currentUser?.id ? "Can't delete yourself" : "Delete user"}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="virsa-card p-6 border-red-200 bg-red-50/50" data-testid="danger-zone">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-red-800">Danger Zone</h2>
            <p className="text-sm text-red-600">Irreversible actions</p>
          </div>
        </div>

        <div className="border-t border-red-200 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-stone-800">Reset Everything</h3>
              <p className="text-sm text-stone-600">
                Delete all recipes, ingredients, rotations, and calendar data. User accounts will be preserved.
              </p>
            </div>
            <button
              onClick={() => {
                setResetStep(1);
                setShowResetDialog(true);
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 whitespace-nowrap"
              data-testid="reset-everything-btn"
            >
              <RefreshCcw size={18} />
              <span>Reset Everything</span>
            </button>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={(open) => {
        if (!open) {
          setShowResetDialog(false);
          setResetStep(1);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-red-600 flex items-center gap-2">
              <AlertTriangle size={24} />
              {resetStep === 1 ? 'Warning: Reset Everything?' : 'Final Confirmation'}
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 
                ? 'This will permanently delete all your data including recipes, ingredients, rotations, and calendar entries.'
                : 'This action cannot be undone. All your data will be permanently deleted. Are you absolutely sure?'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {resetStep === 1 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">
                  <strong>The following will be deleted:</strong>
                </p>
                <ul className="mt-2 text-sm text-amber-700 list-disc list-inside space-y-1">
                  <li>All recipes and their ingredients</li>
                  <li>All ingredients from master list</li>
                  <li>Regular and special rotation schedules</li>
                  <li>Calendar overrides and production plans</li>
                </ul>
                <p className="mt-3 text-amber-800 text-sm">
                  <strong>User accounts will NOT be deleted.</strong>
                </p>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm font-semibold">
                  Type "RESET" to confirm you understand this action is irreversible.
                </p>
                <p className="mt-2 text-red-700 text-sm">
                  All your recipes, ingredients, and schedules will be permanently lost.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <button 
              onClick={() => {
                setShowResetDialog(false);
                setResetStep(1);
              }} 
              className="btn-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleResetEverything}
              disabled={resetting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <span className="spinner" />
              ) : (
                <>
                  <AlertTriangle size={18} />
                  <span>{resetStep === 1 ? 'Continue' : 'Yes, Reset Everything'}</span>
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl" >
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account with a username, password, and role.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Username *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="virsa-input"
                placeholder="Enter username"
                data-testid="new-user-username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Password *</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="virsa-input"
                placeholder="Enter password"
                data-testid="new-user-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Role *</label>
              <Select
                value={formData.role}
                onValueChange={(v) => setFormData({ ...formData, role: v })}
              >
                <SelectTrigger className="h-12" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setShowDialog(false)} className="btn-outline">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving}
              className="btn-primary"
              data-testid="save-user-btn"
            >
              {saving ? <span className="spinner" /> : <Plus size={18} />}
              <span>Create User</span>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowEditDialog(false);
          setEditingUser(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Pencil size={20} />
              Edit User
            </DialogTitle>
            <DialogDescription>
              Update user details. Leave password blank to keep current password.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Username *</label>
              <input
                type="text"
                value={editFormData.username}
                onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                className="virsa-input"
                placeholder="Enter username"
                data-testid="edit-user-username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">
                New Password
                <span className="text-stone-400 font-normal ml-1">(leave blank to keep current)</span>
              </label>
              <input
                type="password"
                value={editFormData.password}
                onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                className="virsa-input"
                placeholder="Enter new password"
                data-testid="edit-user-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Role *</label>
              <Select
                value={editFormData.role}
                onValueChange={(v) => setEditFormData({ ...editFormData, role: v })}
              >
                <SelectTrigger className="h-12" data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editingUser?.id === currentUser?.id && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> You are editing your own account.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <button 
              onClick={() => {
                setShowEditDialog(false);
                setEditingUser(null);
              }} 
              className="btn-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={savingEdit}
              className="btn-primary"
              data-testid="save-edit-user-btn"
            >
              {savingEdit ? <span className="spinner" /> : <Save size={18} />}
              <span>Save Changes</span>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
